// Ana sayfa — Yemek Menüsü'nün altında kompakt duyuru kartı. SADECE personel girişinden
// sonra render edilir (Home, session varken koyar); duyuru yoksa hiç görünmez.
// Önemliler önce (feed sıralı), ilk 2 satır + "+N duyuru daha". Satıra dokununca metin
// açılır; onay bekleyen önemlide "Okudum". Gösterilen satırlar "gördü" sayılır.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Megaphone, AlertTriangle, ChevronDown, Check, CheckCheck } from "lucide-react";
import { cardSlice, needsAck, formatDateTime } from "../../lib/announcements";
import useAnnouncementFeed from "./useAnnouncementFeed";

const MAX_ROWS = 2;

export default function HomeAnnouncementsCard({ isDarkBg, hidden }) {
  const { items, markSeen, ack } = useAnnouncementFeed(true);
  const [expanded, setExpanded] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { shown, rest } = useMemo(() => cardSlice(items, MAX_ROWS, expanded), [items, expanded]);

  // giriş animasyonu sürerken kart gizli (hidden) — o sırada "gördü" sayılmaz
  useEffect(() => { if (!hidden && shown.length) markSeen(shown); }, [hidden, shown, markSeen]);

  if (!items || !items.length) return null;

  const onAck = async (id) => {
    setBusyId(id);
    try {
      await ack(id);
      toast.success("Okundu olarak işaretlendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşaretlenemedi, tekrar deneyin");
    } finally {
      setBusyId(null);
    }
  };

  const tone = isDarkBg
    ? { card: "bg-[#15100c]/90 ring-amber-500/30 text-amber-50", sub: "text-amber-200/60", head: "text-amber-400/80", line: "border-amber-500/15", hover: "hover:bg-amber-500/5" }
    : { card: "bg-white/80 ring-amber-300/60 text-zinc-800 backdrop-blur-md", sub: "text-zinc-500", head: "text-amber-700", line: "border-amber-300/40", hover: "hover:bg-amber-50" };

  return (
    <div className="w-full max-w-xl mb-6" data-testid="home-announcements"
      style={{ visibility: hidden ? "hidden" : "visible" }} aria-hidden={hidden || undefined}>
      <div className={`rounded-2xl ring-1 shadow-lg overflow-hidden ${tone.card}`}>
        <div className={`flex items-center gap-2 px-4 pt-2.5 pb-1 text-[10px] font-black uppercase tracking-widest ${tone.head}`}>
          <Megaphone className="h-3.5 w-3.5" aria-hidden="true" /> Duyurular
        </div>
        <ul>
          {shown.map((a) => {
            const important = a.severity === "important";
            const open = openId === a.id;
            return (
              <li key={a.id} className={`border-t first:border-t-0 ${tone.line}`} data-testid={`home-ann-${a.id}`}>
                <button type="button" onClick={() => setOpenId(open ? null : a.id)} aria-expanded={open}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors ${tone.hover}`}>
                  {important
                    ? <AlertTriangle className="h-4 w-4 text-warning shrink-0" aria-label="Önemli" />
                    : <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0 mx-[5px]" aria-hidden="true" />}
                  <span className={`min-w-0 flex-1 text-sm font-bold ${open ? "break-words" : "truncate"}`}>{a.title}</span>
                  {needsAck(a) && <span className="shrink-0 rounded-full border border-warning px-1.5 text-[10px] font-bold text-warning">Onay bekliyor</span>}
                  <ChevronDown className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {open && (
                  <div className="px-4 pb-3 -mt-0.5 space-y-2" data-testid={`home-ann-body-${a.id}`}>
                    <p className="text-sm whitespace-pre-line break-words">{a.body}</p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className={`text-[11px] ${tone.sub}`}>{a.created_by_name} · {formatDateTime(a.updated_at || a.created_at)}</span>
                      {needsAck(a) && (
                        <button type="button" onClick={() => onAck(a.id)} disabled={busyId === a.id}
                          className="inline-flex items-center gap-1 rounded-md bg-warning px-3 py-1 text-xs font-bold text-black hover:bg-warning/90 disabled:opacity-60"
                          data-testid={`home-ann-ack-${a.id}`}>
                          <Check className="h-3.5 w-3.5" aria-hidden="true" /> Okudum
                        </button>
                      )}
                      {important && a.me?.acked && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success"><CheckCheck className="h-3.5 w-3.5" /> Okudunuz</span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {(rest > 0 || expanded) && items.length > MAX_ROWS && (
          <button type="button" onClick={() => setExpanded((x) => !x)}
            className={`w-full border-t px-4 py-1.5 text-xs font-bold text-left ${tone.line} ${tone.head} ${tone.hover}`}
            data-testid="home-ann-more">
            {expanded ? "Daha az göster" : `+${rest} duyuru daha`}
          </button>
        )}
      </div>
    </div>
  );
}
